import React from 'react';
import { statusColors } from '../../data/mock';
import { CheckCircle2, Clock, AlertTriangle, AlertOctagon, Info, Star, MapPin } from 'lucide-react';

const AppShowcase = () => {
  return (
    <section className="py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[#121212]" />
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-[7.6923%]">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Status Legend */}
          <div className="space-y-8">
            <div>
              <span className="text-[#00FFD1] text-sm font-medium tracking-wider uppercase">Visual Intelligence</span>
              <h2 className="text-4xl md:text-5xl font-semibold text-white mt-4 leading-tight">
                See Everything
                <span className="block text-white/50">At a Glance.</span>
              </h2>
              <p className="text-white/60 text-xl mt-6 leading-relaxed">
                Color-coded status indicators tell you instantly which stores need attention—no digging, no guessing.
              </p>
            </div>
            
            {/* Status Legend */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#000000] border border-white/10 p-5 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#22c55e] flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white font-medium">Visited Today</span>
                </div>
                <p className="text-white/50 text-sm">Store is active and up to date</p>
              </div>
              
              <div className="bg-[#000000] border border-white/10 p-5 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white font-medium">Under 30 Days</span>
                </div>
                <p className="text-white/50 text-sm">Recent visit, still in good standing</p>
              </div>
              
              <div className="bg-[#000000] border border-white/10 p-5 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#f97316] flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white font-medium">Under 45 Days</span>
                </div>
                <p className="text-white/50 text-sm">Schedule a visit soon</p>
              </div>
              
              <div className="bg-[#000000] border border-white/10 p-5 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#ef4444] flex items-center justify-center">
                    <AlertOctagon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white font-medium">Over 45 Days</span>
                </div>
                <p className="text-white/50 text-sm">Overdue—needs immediate attention</p>
              </div>
            </div>
            
            {/* Badge Legend */}
            <div className="bg-[#000000] border border-white/10 p-6">
              <h4 className="text-white font-semibold mb-4">Quick Action Badges</h4>
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-[#3b82f6] flex items-center justify-center text-white text-sm font-bold">N</span>
                  <span className="text-white/60 text-sm">Notes</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-[#14b8a6] flex items-center justify-center text-white text-sm font-bold">P</span>
                  <span className="text-white/60 text-sm">History</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-[#eab308] flex items-center justify-center text-black text-sm font-bold">I</span>
                  <span className="text-white/60 text-sm">Invoice</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-8 h-8 text-white/50" />
                  <span className="text-white/60 text-sm">Navigate</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right - App Interface Mockup */}
          <div className="relative">
            {/* Dashboard Mockup */}
            <div className="bg-[#000000] border border-white/10 rounded-lg overflow-hidden shadow-2xl">
              {/* Header */}
              <div className="bg-[#121212] px-6 py-4 border-b border-white/10">
                <h3 className="text-white font-semibold">Dashboard Overview</h3>
              </div>
              
              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[#121212] p-4 text-center">
                    <p className="text-3xl font-bold text-[#22c55e]">47</p>
                    <p className="text-white/50 text-sm">Active Stores</p>
                  </div>
                  <div className="bg-[#121212] p-4 text-center">
                    <p className="text-3xl font-bold text-[#f97316]">12</p>
                    <p className="text-white/50 text-sm">30+ Days</p>
                  </div>
                  <div className="bg-[#121212] p-4 text-center">
                    <p className="text-3xl font-bold text-[#ef4444]">5</p>
                    <p className="text-white/50 text-sm">45+ Days</p>
                  </div>
                </div>
                
                {/* Route Summary */}
                <div className="bg-[#121212] p-4">
                  <h4 className="text-white/70 text-sm mb-4">Sales by Route</h4>
                  <div className="space-y-3">
                    {['Dallas', 'Houston', 'Austin'].map((route, i) => (
                      <div key={route} className="flex items-center justify-between">
                        <span className="text-white">{route}</span>
                        <div className="flex items-center gap-3">
                          <div className="w-32 h-2 bg-white/10 overflow-hidden">
                            <div 
                              className="h-full bg-[#00FFD1]" 
                              style={{ width: `${90 - i * 20}%` }}
                            />
                          </div>
                          <span className="text-white/60 text-sm">${(4200 - i * 1000).toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Recent Invoices */}
                <div className="bg-[#121212] p-4">
                  <h4 className="text-white/70 text-sm mb-4">Recent Invoices</h4>
                  <div className="space-y-2">
                    {[
                      { store: 'Valero - Hampton', amount: 247.50, date: '11-11-2023' },
                      { store: 'QS Liquor', amount: 189.00, date: '12-12-2023'  },
                      { store: 'Shell - Main St', amount: 312.75, date: '10-10-2023'  },
                    ].map((inv, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                        <div>
                          <p className="text-white text-sm">{inv.store}</p>
                          <p className="text-white/40 text-xs">{inv.time}</p>
                        </div>
                        <span className="text-[#00FFD1] font-medium">${inv.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating Badge */}
            <div className="absolute -top-4 -right-4 bg-[#00FFD1] px-4 py-2 shadow-lg">
              <span className="text-black font-bold text-sm">LIVE DATA</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppShowcase;
